import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { countBy, merge } from 'lodash';
import { KeyboardKeyCodes } from 'common/common/constants/keyboard-key-codes';
import { AssigneeTypes, bcEnums } from 'common/common/constants/boldchat-enums';
import { WorkItemAvailabilityType, WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { TranslateService } from '@ngx-translate/core';
import { OperatorsListService } from './operators-list.service';
import { BcLocalKeyboardHandlerFactory } from 'common/keyboard-service/local-keyboard-handler.factory';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ItemType } from 'shared/components/operators-list/item-type';

@Component({
  selector: 'app-operators-list',
  templateUrl: './operators-list.component.html',
  styleUrls: ['./operators-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class OperatorsListComponent implements OnInit {
  private _onlineOperators;

  public departments = [];
  public operatorsActiveChatCount;
  public operatorsActiveTextChatCount;
  public searchField;
  public unfilteredDepartments;
  public acdItem;
  public selectedItem;
  public activeListItemIndex;
  public keyboardHandler;
  public keyboardKeys;
  public activeChats;
  public activeMessages;
  public listItems;

  public readonly LIST_ITEM_SIZE = 40;
  public readonly OPERATOR_ITEM_TYPE = ItemType.OPERATOR_ITEM_TYPE;
  public readonly DEPARTMENT_ITEM_TYPE = ItemType.DEPARTMENT_ITEM_TYPE;

  @Input()
  public workItemType: string;
  @Input()
  public isAcdAvailable: boolean;
  @Input()
  public isDepartmentSelectable: boolean;
  @Input()
  public isOfflineItemSelectable: boolean;
  @Output()
  public onOperatorSelected: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  public onSubmitActionTriggered: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('searchInput', { static: true })
  searchInput: ElementRef;

  @ViewChild('virtualOperatorList', { static: true })
  virtualOperatorList: CdkVirtualScrollViewport;

  constructor(private element: ElementRef,
              private _operatorsListService: OperatorsListService,
              private bcLocalKeyboardHandlerFactory: BcLocalKeyboardHandlerFactory,
              private _translateService: TranslateService,
              private _changeDetector: ChangeDetectorRef) {
    this.searchField = '';
    this.selectedItem = null;
    this.acdItem = {
      availability: {
        chat: bcEnums.statuses.AVAILABLE,
        email: bcEnums.statuses.AVAILABLE,
        sms: bcEnums.statuses.AVAILABLE,
        facebook: bcEnums.statuses.AVAILABLE,
        asyncMessage: bcEnums.statuses.AVAILABLE
      },
      operators: [],
      id: 'ACD',
      selected: false
    };

    this.keyboardHandler = this.bcLocalKeyboardHandlerFactory.createFromElement(this.element.nativeElement);
    this.keyboardKeys = KeyboardKeyCodes;

    this.activeListItemIndex = 0;

    this._onlineOperators = [];
    this.operatorsActiveChatCount = new Map();
    this.operatorsActiveTextChatCount = new Map();
  }

  ngOnInit() {
    this._loadList();

    this.searchInput.nativeElement.focus();

    this.keyboardHandler.addKeyHandler('up', () => this._decreaseSelectedIndex());
    this.keyboardHandler.addKeyHandler('down', () => this._increaseSelectedIndex());
    this.keyboardHandler.addKeyHandler('enter', () => this._enterKeyPressed());
    this.keyboardHandler.addKeyHandler('left', () => this.toggleDepartmentCollapsed(this.listItems[this.activeListItemIndex].item, true));
    this.keyboardHandler.addKeyHandler('right', () => this.toggleDepartmentCollapsed(this.listItems[this.activeListItemIndex].item, false));
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.virtualOperatorList.checkViewportSize();
  }

  public selectItem(item) {
    if (!this.isItemSelectable(item)) {
      return;
    }
    if (this.selectedItem) {
      this.selectedItem.selected = false;
    }
    if (this.selectedItem && this.selectedItem.id === item.id) {
      item.selected = false;
      this.selectedItem = null;
    } else {
      item.selected = true;
      this.selectedItem = item;
    }

    this._emitOnOperatorSelected();
  }

  public unselectItem() {
    if (this.selectedItem) {
      this.selectItem(this.selectedItem);
    }
  }

  public toggleDepartmentCollapsed(department, toFixed?) {
    if (department.availability[this.getWorkItemAvailabilityKey()] !== 0) {
      if (toFixed !== undefined) {
        department.collapsed = toFixed;
      } else {
        department.collapsed = !department.collapsed;
      }
      this.filterList();
      this._changeDetector.detectChanges();
    }
  }

  public trackByItemId(index, listItem): string {
    return listItem.item.id;
  }

  public getUnfilteredOperatorCount(department) {
    return department.unfilteredOperatorCount;
  }

  public getWorkItemAvailabilityKey() {
    return WorkItemAvailabilityType[this.workItemType];
  }

  public getAvailableOperatorCount(department) {
    const statusMap = countBy(department.operators, 'availability.' + this.getWorkItemAvailabilityKey());
    return statusMap[bcEnums.statuses.AVAILABLE] || 0;
  }

  public getItemDisplayName(listItem): string {
    if (listItem.type === this.DEPARTMENT_ITEM_TYPE) {
      if (listItem.item === this.acdItem) {
        return this._translateService.instant('operatorsList.assignAutomaticallyLabel');
      } else if (!listItem.item.name) {
        return this._translateService.instant('operatorsList.noDepartment');
      }
    }
    return listItem.item.name;
  }

  public searchFilterChanged() {
    this.filterList();
    this.setSelectedIndex(0);
    this._scrollActiveElementIntoView(true);
  }

  public filterList() {
    if(this.isAcdAvailable){
      this.departments = [this.acdItem].concat(this.unfilteredDepartments);
    } else {
      this.departments = this.unfilteredDepartments;
    }

    this.listItems = this._operatorsListService.buildListFilteredByName(this.departments, this.searchField);

    this._changeDetector.detectChanges();
    this.virtualOperatorList.checkViewportSize();
  }

  public getListItemClass(index, listItem) {
    const itemType = listItem.type;
    let classList = '';
    if (listItem.item.selected) {
      classList += ' selected';
    }
    if (index === this.activeListItemIndex) {
      classList += ' key-cursor';
    }
    if (itemType === this.DEPARTMENT_ITEM_TYPE && !listItem.item.name && listItem.item !== this.acdItem) {
      classList += ' no-department';
    }
    if (listItem.item === this.acdItem || itemType === this.OPERATOR_ITEM_TYPE) {
      classList += ' bc-no-icon';
    }
    if (this._isDepartmentNotSelectable(listItem.item)) {
      classList += ' not-selectable';
    }
    if (itemType === this.DEPARTMENT_ITEM_TYPE) {
      classList += ' department-item';
    }
    if (itemType === this.OPERATOR_ITEM_TYPE) {
      classList += ' operator-item';
    }
    return classList;
  }

  public setSelectedIndex(idx) {
    this.activeListItemIndex = idx;
  }

  public resetSearch() {
    this.searchField = '';
    this.resetSelection();
    this.filterList();
  }

  public resetSelection() {
    const scrollElement = this.element.nativeElement.querySelector('.operators-panel-list');
    scrollElement.scrollTop = 0;

    this.unselectItem();
    this.activeListItemIndex = 0;
  }

  public resetOperatorList() {
    this.resetSelection();
    this._loadList();
  }

  public getOperatorActiveItemsCount(operatorId: string): number {
    return this._getOperatorActiveChatCount(operatorId) + this._getOperatorActiveTextChatCount(operatorId);
  }

  public createTooltipText(listItem): string {
    if (listItem.type !== this.OPERATOR_ITEM_TYPE) {
      return;
    }
    const operatorId = listItem.item.id;
    const operatorAvailability = listItem.item.availability[this.getWorkItemAvailabilityKey()];
    this.activeChats = this._getOperatorActiveChatCount(operatorId);
    this.activeMessages = this._getOperatorActiveTextChatCount(operatorId);
    let translatedTooltipForChat = this._translateService.instant('operatorsList.tooltipAssignedChats', { activeChats: this.activeChats });
    let translatedTooltipForMessage = this._translateService.instant('operatorsList.tooltipMessages', { activeMessages: this.activeMessages });
    if (operatorAvailability === bcEnums.statuses.AWAY) {
      return this._translateService.instant('operatorsList.away');
    } else {
      if (this.activeMessages === 0 && this.activeChats === 0) {
        return this._translateService.instant('operatorsList.free');
      } else if (this.activeChats > 0 && this.activeMessages > 0) {
        return translatedTooltipForChat + ' ' + translatedTooltipForMessage;
      } else if (this.activeChats > 0 && this.activeMessages === 0) {
        return translatedTooltipForChat;
      } else if (this.activeChats === 0 && this.activeMessages > 0) {
        return translatedTooltipForMessage;
      }
    }
  }

  public isItemCountDisplayed(listItem): boolean {
    if (listItem.type !== this.OPERATOR_ITEM_TYPE) {
      return false;
    }
    const operatorAvailability = listItem.item.availability[this.getWorkItemAvailabilityKey()];
    const operatorId = listItem.item.id;
    return operatorAvailability === bcEnums.statuses.AVAILABLE && this.getOperatorActiveItemsCount(operatorId) > 0;
  }

  private async _loadList() {
    await this._operatorsListService.getDepartmentOperatorAvailabilityList()
      .then(departments => this._filterOperators(departments))
      .then(departments => {
        this.unfilteredDepartments = departments;
        this.filterList();
      });

    this._initOnlineOperatorsList();
    this._initOperatorsActiveChatCountMap();
    this._initOperatorsActiveTextChatCountMap();
  }

  private _decreaseSelectedIndex() {
    if (this.activeListItemIndex <= 0) {
      this.activeListItemIndex = 0; // Fix index in case it's out of bounds
      return;
    }
    this.activeListItemIndex--;
    this._scrollActiveElementIntoView(true);
    this._changeDetector.detectChanges();
  }

  private _increaseSelectedIndex() {
    if (this.activeListItemIndex >= this.listItems.length - 1) {
      this.activeListItemIndex = this.listItems.length - 1; // Fix index in case it's out of bounds
      return;
    }
    this.activeListItemIndex++;
    this._scrollActiveElementIntoView(false);
    this._changeDetector.detectChanges();
  }

  private _scrollActiveElementIntoView(scrollToTop: boolean) {
    const currentListItem = this.element.nativeElement.querySelector(`.list-item-${this.activeListItemIndex}`);
    const isElementInDom = !!currentListItem;
    let isElementOutsideViewport = false;

    if (isElementInDom) {
      const scrollElement = this.element.nativeElement.querySelector('.operators-panel-list');
      let elementBounds = currentListItem.getBoundingClientRect();
      let viewportBounds = scrollElement.getBoundingClientRect();

      if (scrollToTop) {
        isElementOutsideViewport = (elementBounds.top < viewportBounds.top);
      } else {
        isElementOutsideViewport = (elementBounds.bottom > viewportBounds.bottom);
      }
    }

    if (!isElementInDom || isElementOutsideViewport) {
      if (scrollToTop) {
        this.virtualOperatorList.scrollToIndex(this.activeListItemIndex, 'smooth');
      } else {
        const targetOffset = Math.max(0, (this.activeListItemIndex + 1) * this.LIST_ITEM_SIZE - this.virtualOperatorList.getViewportSize());
        this.virtualOperatorList.scrollToOffset(targetOffset, 'smooth');
      }
    }
  }

  private _filterOperators(departments) {
    if (this.workItemType === WorkItemTypes.EMAIL) {
      return departments;
    }

    return this._operatorsListService.filterOfflineOperators(departments, WorkItemTypes.CHAT);
  }

  private _isServiceUnavailable(item): boolean {
    if (this.workItemType === WorkItemTypes.SMS) {
      return item.availability.sms === bcEnums.statuses.OFFLINE;
    } else if (this.workItemType === WorkItemTypes.FACEBOOK) {
      return item.availability.facebook === bcEnums.statuses.OFFLINE;
    } else if (this.workItemType === WorkItemTypes.ASYNC_MESSAGE) {
      return item.availability.asyncMessage === bcEnums.statuses.OFFLINE;
    } else {
      return item.availability.chat === bcEnums.statuses.OFFLINE;
    }
  }

  private _enterKeyPressed() {
    this._selectItemWithKeyboard();
    this.onSubmitActionTriggered.emit();
  }

  private _selectItemWithKeyboard() {
    const listItem = this.listItems[this.activeListItemIndex];
    if (listItem.type === this.OPERATOR_ITEM_TYPE || this.isDepartmentSelectable) {
      this.selectItem(listItem.item);
      this._emitOnOperatorSelected();
    }
  }

  private _isDepartmentSelected(selectedItem): boolean {
    return !!selectedItem.operators;
  }

  private _emitOnOperatorSelected(): void {
    const extendedSelectedItem = merge({}, this.selectedItem, {
      selectedItemType: this._getSelectedItemType()
    });
    this.onOperatorSelected.emit(extendedSelectedItem);
  }

  private _getSelectedItemType(): string {
    if (this.selectedItem) {
      if (this.selectedItem.id === 'ACD') {
        return AssigneeTypes.ACD;
      }
      if (this._isDepartmentSelected(this.selectedItem)) {
        return AssigneeTypes.DEPARTMENT;
      }
      return AssigneeTypes.OPERATOR;
    }
  }

  private _isItemWithNoDepartment(item): boolean {
    return item.id === bcEnums.departments.NO_DEPARTMENT_ID;
  }

  private _isOfflineItemNotSelectable(item): boolean {
    return !this.isOfflineItemSelectable && this._isServiceUnavailable(item);
  }

  private _isDepartmentNotSelectable(item): boolean {
    return !this.isDepartmentSelectable && !!item.operators;
  }

  public isItemSelectable (item): boolean {
    return !(this._isItemWithNoDepartment(item) || this._isOfflineItemNotSelectable(item) || this._isDepartmentNotSelectable(item));
  }

  private _initOnlineOperatorsList(): void {
    this.departments.forEach(dep => {
      dep.operators.forEach(op => {
        if (!this._onlineOperators.includes(op.id) && op.availability[this.getWorkItemAvailabilityKey()] === bcEnums.statuses.AVAILABLE) {
          this._onlineOperators.push(op.id);
        }
      });
    });
  }

  private _initOperatorsActiveChatCountMap(): void {
    if (this._onlineOperators.length > 0) {
      this._operatorsListService.getActiveChatCounts()
        .then(opsActiveChatCount => opsActiveChatCount
          .filter(opActiveChatCount => this._onlineOperators.includes(opActiveChatCount.operatorId))
          .forEach(opActiveChatCount => {
            this.operatorsActiveChatCount.set(opActiveChatCount.operatorId, opActiveChatCount.activeWorkItemCount);
          })
        );
    }
  }

  private _initOperatorsActiveTextChatCountMap(): void {
    if (this._onlineOperators.length > 0) {
      this._operatorsListService.getActiveTextChatCounts()
        .then(opsActiveTextChatCount => opsActiveTextChatCount
          .filter(opActiveTextChatCount => this._onlineOperators.includes(opActiveTextChatCount.operatorId))
          .forEach(opActiveTextChatCount => {
            this.operatorsActiveTextChatCount.set(opActiveTextChatCount.operatorId, opActiveTextChatCount.activeWorkItemCount);
          })
        );
    }
  }

  private _getOperatorActiveChatCount(operatorId: string): number {
    return this.operatorsActiveChatCount.get(operatorId);
  }

  private _getOperatorActiveTextChatCount(operatorId: string): number {
    return this.operatorsActiveTextChatCount.get(operatorId);
  }
}
